/**
 * `/internal/customer-managers/*` contract types.
 *
 * Customer-manager ownership is org-scoped. A tenant can have multiple
 * accounts/members over time, but the operational owner belongs to the
 * customer organization, not to one account row.
 */

import type { BrandId } from './brands.ts';
import type {
  AdminOrgSkuRate,
  GetAccountDetailResponse,
  RevokeAdminApiKeyResponse,
  SalesAccountDetailResponse,
  SalesOrgSkuRate,
  SalesUpdateAccountSkuRateResponse,
  UpdateAccountSkuRateResponse,
  UpdateOrgSkuVisibilityResponse,
} from './internal-admin.ts';
import type { ApprovalRequestRecord } from './internal-approvals.ts';
import type {
  IssueDirectGiftResponse,
  RedeemGiftCardOnBehalfResponse,
} from './internal-gift-cards.ts';
import type { OpsRunwayBurnWindow } from './internal-ops-runway.ts';
import type { OpsLoginMethod } from './internal-ops-users.ts';
import type { RequestLifecycleStatus } from './internal-request-lifecycle.ts';
import type { OpsSupportRequestRow } from './ops-support.ts';

export interface CustomerManagerAssignmentRecord {
  org_id: string;
  customer_manager_ops_user_id: string;
  assigned_by_ops_user_id: string;
  assigned_at: string;
  revoked_at: string | null;
  revoked_by_ops_user_id: string | null;
  updated_at: string;
}

export interface CustomerManagerCustomerSummary {
  account_id: string;
  email: string;
  org_id: string;
  org_name: string;
  balance_usd_micros?: number;
  balance_usd_cents: number;
  /**
   * The account's own RAW credit headroom in micro-USD
   * (`accounts.credit_limit_usd_micros`, epic #1033 E4-C3, #1127). Optional /
   * additive; absent or 0 = no credit line. NEVER the inherited
   * `credit_settings` default. The `low_balance` risk signal classifies by
   * `balance + this` so a credit customer with headroom is not flagged.
   */
  credit_limit_usd_micros?: number;
  created_at: string;
  /**
   * Operational customer-brand authority (`accounts.signup_brand`, epic
   * #1030 E1-S4). Sales books are partitioned by this value; admin can
   * correct it via `/internal/admin/accounts/:accountId/signup-brand`.
   */
  signup_brand: BrandId;
  customer_manager_ops_user_id: string | null;
  customer_manager_email: string | null;
  customer_manager_display_name: string | null;
  /**
   * Sign-in methods of the current customer manager (router: ops-user
   * disambiguation), `[]` when unassigned. Additive; absent on older
   * platform builds.
   */
  customer_manager_login_methods?: OpsLoginMethod[];
  /** Readable email of the current customer manager (synthetic `@ops.internal` hidden), additive. */
  customer_manager_contact_email?: string | null;
  assigned_at: string | null;
  /** CRM contact card company (`customer_contacts.company`), additive since identity-labels; absent on older platform builds. */
  company?: string | null;
  /** CRM contact card person name (`customer_contacts.contact_name`), additive. */
  contact_name?: string | null;
}

export interface ListCustomerManagerCustomersResponse {
  customers: CustomerManagerCustomerSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetCustomerManagerCustomerResponse {
  customer: CustomerManagerCustomerSummary;
}

export interface AssignCustomerManagerRequest {
  customer_manager_ops_user_id: string;
  actor_user_id: string;
}

export interface AssignCustomerManagerResponse {
  assignment: CustomerManagerAssignmentRecord;
}

export interface ClaimCustomerManagerRequest extends AssignCustomerManagerRequest {
  /**
   * When present, the claim only succeeds if the pool customer's
   * `signup_brand` equals this brand (epic #1030 E1-S4). ops-api injects
   * the claimant's server-side sales-profile brand here for non-admin
   * sales sessions; the value is validated against the brand registry.
   */
  required_brand?: string;
}

export type ClaimCustomerManagerResponse = AssignCustomerManagerResponse;

export interface RevokeCustomerManagerRequest {
  actor_user_id: string;
}

export interface RevokeCustomerManagerResponse {
  assignment: CustomerManagerAssignmentRecord;
}

export interface CustomerManagerWorkbenchResponse {
  owner: {
    ops_user_id: string;
    display_name: string;
  };
  portfolio: {
    assigned_customers: number;
    total_balance_usd_micros?: number;
    total_balance_usd_cents: number;
    active_api_keys: number;
    requests_30d: number;
    errors_30d: number;
    customers_with_risk: number;
  };
  risk_queue: CustomerManagerRiskQueueItem[];
  customers: CustomerManagerPortfolioCustomer[];
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
}

export interface CustomerManagerCustomerPoolResponse {
  pool: {
    available_customers: number;
    total_balance_usd_micros?: number;
    total_balance_usd_cents: number;
    active_api_keys: number;
    requests_30d: number;
    errors_30d: number;
    customers_with_risk: number;
  };
  customers: CustomerManagerPortfolioCustomer[];
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
}

export interface CustomerManagerUsagePostureRecord {
  org_id: string;
  usage: CustomerManagerUsagePosture;
  recent_failures: CustomerManagerRecentFailureSummary;
}

export interface CustomerManagerUsagePostureResponse {
  customers: CustomerManagerUsagePostureRecord[];
  from: string;
  to: string;
  generated_at: string;
}

export interface CustomerManagerApiKeyPostureRecord {
  org_id: string;
  api_keys: CustomerManagerApiKeyPosture;
}

export interface CustomerManagerApiKeyPostureResponse {
  customers: CustomerManagerApiKeyPostureRecord[];
  generated_at: string;
}

export type CustomerManagerRiskSignalCode =
  | 'low_balance'
  | 'no_active_api_key'
  | 'stale_api_key'
  | 'recent_errors'
  | 'no_recent_usage'
  /**
   * Owned-book finance fact (epic #1035 E6-D6, #1070): the customer has
   * unresolved billing anomalies. Merged into the workbench queue from
   * the platform anomaly backlog — read-only for sales (resolve/replay
   * stays admin, #1067).
   */
  | 'finance_billing_anomaly'
  /**
   * Dunning facts (epic #1033 E4-C7, #1131): the customer has open
   * `billing_statements` past their due date (`finance_ar_overdue`) or due
   * within the reminder horizon (`finance_ar_due_soon`), with live debt still
   * outstanding. Merged into the workbench queue from the platform dunning
   * feed; suppressed once the account's current debt position reaches zero
   * (prepaid-recovered), even while open statement rows remain.
   */
  | 'finance_ar_overdue'
  | 'finance_ar_due_soon';

export interface CustomerManagerRiskSignal {
  code: CustomerManagerRiskSignalCode;
  severity: 'info' | 'warning' | 'critical';
  label: string;
  detail: string;
}

export interface CustomerManagerOwnerSummary {
  ops_user_id: string | null;
  email: string | null;
  display_name: string | null;
  /** Mirrors `customer_manager_login_methods` on the customer summary; additive. */
  login_methods?: OpsLoginMethod[];
  /** Mirrors `customer_manager_contact_email` on the customer summary; additive. */
  contact_email?: string | null;
  assigned_at: string | null;
}

export interface CustomerManagerUsagePosture {
  window_days: number;
  request_count: number;
  error_count: number;
  cost_usd_micros?: number;
  cost_usd_cents: number;
  input_tokens: number;
  output_tokens: number;
  last_request_at: string | null;
}

export interface CustomerManagerApiKeyPosture {
  total: number;
  active: number;
  revoked: number;
  last_used_at: string | null;
}

export interface CustomerManagerRecentFailureSummary {
  count: number;
  latest_request_id: string | null;
  latest_status_code: number | null;
  latest_at: string | null;
}

export interface CustomerManagerPortfolioCustomer {
  org_id: string;
  org_name: string;
  account_id: string;
  email: string;
  created_at: string;
  balance_usd_micros?: number;
  balance_usd_cents: number;
  /** Operational customer-brand authority (epic #1030 E1-S4). */
  signup_brand: BrandId;
  owner: CustomerManagerOwnerSummary;
  usage: CustomerManagerUsagePosture;
  api_keys: CustomerManagerApiKeyPosture;
  recent_failures: CustomerManagerRecentFailureSummary;
  risk_signals: CustomerManagerRiskSignal[];
}

export interface CustomerManagerRiskQueueItem {
  org_id: string;
  account_id: string;
  email: string;
  primary_signal: CustomerManagerRiskSignal;
  risk_count: number;
}

export interface CustomerManagerApiKeyDetail {
  id: string;
  name: string | null;
  key_prefix: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  channel_id: string | null;
  channel_slug: string | null;
  channel_display_name: string | null;
}

export interface CustomerManagerLifecycleEvidence {
  request_id: string;
  status_code: number;
  lifecycle_status: string | null;
  stream: boolean | null;
  upstream_attempts: number | null;
  trace_available: boolean;
  created_at: string;
}

/**
 * Customer 360 — role-projected sku-rate layers (epic #1032 E3-P2,
 * #1104). `sku_rate_view` is the wire discriminant: ops-api resolves it
 * from the SESSION ROLES (admin / sales lead ⇒ `full`, everyone else ⇒
 * `sales`), never from a client parameter. The sales variant embeds the
 * sales-projected account detail too — `account.org.sku_rates` would
 * otherwise leak the same procurement fields through the side door.
 */
export interface CustomerManagerCustomer360BaseResponse {
  customer: CustomerManagerPortfolioCustomer;
  api_keys: CustomerManagerApiKeyDetail[];
  recent_requests: CustomerManagerLifecycleEvidence[];
}

export interface CustomerManagerCustomer360FullResponse
  extends CustomerManagerCustomer360BaseResponse {
  sku_rate_view: 'full';
  account: GetAccountDetailResponse;
  sku_rates: AdminOrgSkuRate[];
}

export interface CustomerManagerCustomer360SalesResponse
  extends CustomerManagerCustomer360BaseResponse {
  sku_rate_view: 'sales';
  account: SalesAccountDetailResponse;
  sku_rates: SalesOrgSkuRate[];
}

export type CustomerManagerCustomer360Response =
  | CustomerManagerCustomer360FullResponse
  | CustomerManagerCustomer360SalesResponse;

export type CustomerManagerRequestLogRow = OpsSupportRequestRow;

export interface CustomerManagerRequestLogResponse {
  customer: CustomerManagerCustomerSummary;
  rows: CustomerManagerRequestLogRow[];
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
  errors?: readonly string[];
}

export interface CustomerManagerPortfolioRequestLogResponse {
  customers: CustomerManagerCustomerSummary[];
  rows: CustomerManagerRequestLogRow[];
  total: number;
  limit: number;
  offset: number;
  generated_at: string;
  errors?: readonly string[];
}

/**
 * Deliberately narrow lifecycle projection for the customer-manager surface.
 *
 * Full lifecycle evidence includes serving-node, routing partition/config,
 * route-source, and upstream identity. Those fields are admin-only and must
 * remain available exclusively through `/v1/ops/request-lifecycle/:requestId`.
 * Keep this contract as an allowlist rather than deriving it from the admin
 * response so future internal fields fail closed.
 */
export interface CustomerManagerRequestLifecycleMetric {
  requestId: string;
  lifecycleStatus: RequestLifecycleStatus;
  statusCode: number | null;
  lastPhase: string;
  totalMs: number | null;
  upstreamDurationMs: number | null;
  billingDurationMs: number | null;
  clientResponseDurationMs: number | null;
  traceStatus: string | null;
  /**
   * Whether this request's event batches were kept. NULL on rows written
   * before sampling existed.
   */
  sampled: boolean | null;
  /**
   * Sample probability used for the keep decision. NULL on pre-sampling rows.
   */
  sampleRate: number | null;
}

export interface CustomerManagerRequestLifecycleEvent {
  eventId: string;
  requestId: string;
  phase: string;
  eventTs: string;
  elapsedMs: number;
  durationMs: number | null;
  attempt: number | null;
  statusCode: number | null;
  outcome: string | null;
  errorCode: string | null;
}

export interface CustomerManagerRequestLifecycleResponse {
  metric: CustomerManagerRequestLifecycleMetric;
  events: CustomerManagerRequestLifecycleEvent[];
  /**
   * Present when the request was not sampled and has no stored events, so an
   * empty timeline is not mistaken for "no events were recorded".
   */
  timeline_unsampled?: true;
}
export type CustomerManagerRevokeApiKeyResponse = RevokeAdminApiKeyResponse;
export type CustomerManagerRedeemGiftCardResponse = RedeemGiftCardOnBehalfResponse;
/**
 * `POST /v1/ops/v2/customer-manager/customers/:orgId/sku-rates` —
 * discriminated write outcome (epic #1032 E3-P3, #1106, mirroring the
 * gift-issue envelope):
 *
 *   - `applied` — the override was written. The echoed `result` is
 *     role-projected like the 360 read (#1104): full shape for
 *     admin/lead sessions, sales projection for regular CM sessions.
 *   - `approval_pending` — the CM's rate landed below the sales floor
 *     (at/above procurement, validated server-side): the write was NOT
 *     applied and a pending `discount_below_sales_cost` approval was
 *     created for the same-brand lead instead. Only sales sessions can
 *     observe this variant; admin/lead writes never convert.
 */
export type CustomerManagerUpdateSkuRateResponse =
  | {
      outcome: 'applied';
      result: UpdateAccountSkuRateResponse | SalesUpdateAccountSkuRateResponse;
    }
  | { outcome: 'approval_pending'; request: ApprovalRequestRecord };

// ─── targeted direct-credit gift with quota gate (epic #1032 E3-G2, #1103) ──

/**
 * `POST /v1/ops/v2/customer-manager/customers/:orgId/gifts/issue`.
 *
 * The CM-scoped gift path: NO bearer code exists at any point (the CM
 * bearer-code issue route was retired with the interim cap). Expiry is
 * always the configured default (`gift_quota_settings.default_expiry_days`)
 * — the payload deliberately carries no expiry so the within-quota and
 * approval-executed paths stay consistent. `external_request_id` is an
 * optional idempotency key for the within-quota credit; omitted → a
 * fresh UUID per submission.
 */
export interface CustomerManagerIssueGiftRequest {
  /** Positive whole-yuan integer RMB cents. */
  amount_cny_cents: number;
  reason?: string;
  external_request_id?: string;
}

/** Quota context echoed on the over-quota outcome (read-model values). */
export interface CustomerManagerGiftQuotaContext {
  month: string;
  monthly_quota_cny_cents: number;
  occupied_cny_cents: number;
  remaining_cny_cents: number;
}

/**
 * Discriminated outcome: within remaining quota the atomic G1 credit
 * ran (`issued`); over quota — including losing the last-headroom race
 * to a concurrent issuance — a pending `gift_over_quota` approval was
 * created instead and NO money moved (`approval_pending`). Admin
 * sessions bypass the quota gate and always observe `issued`.
 */
export type CustomerManagerIssueGiftResponse =
  | { outcome: 'issued'; issuance: IssueDirectGiftResponse }
  | {
      outcome: 'approval_pending';
      request: ApprovalRequestRecord;
      quota: CustomerManagerGiftQuotaContext;
    };

export type CustomerManagerUpdateSkuVisibilityResponse = UpdateOrgSkuVisibilityResponse;

// ─── workbench money views (epic #1035, #1065) ──────────────────────

/**
 * Trend windows accepted by `GET /v1/ops/v2/customer-manager/money?days=`.
 * The aggregate blocks (top customers + funnel) always use the fixed
 * 30-day window regardless of the trend selection.
 */
export const CUSTOMER_MANAGER_MONEY_TREND_DAY_OPTIONS = [30, 90] as const;
export type CustomerManagerMoneyTrendDays =
  (typeof CUSTOMER_MANAGER_MONEY_TREND_DAY_OPTIONS)[number];

/** Fixed window for the top-customers + funnel aggregate blocks. */
export const CUSTOMER_MANAGER_MONEY_AGGREGATE_WINDOW_DAYS = 30;

/** Top-customers rows returned on the money view. */
export const CUSTOMER_MANAGER_MONEY_TOP_CUSTOMER_LIMIT = 10;

/**
 * One UTC+8 business-day bucket of the owned-book consumption trend.
 * Revenue lens is tenant charge (`usd_charged`) only — the sales
 * projection deliberately carries no procurement cost, upstream
 * identity, or L1/L2 margin fields (issue #1065 AC 4). CNY display is
 * derived client-side from the `exchange_rate_policy.cny_per_usd`
 * pivot and labeled as consumption (消费), never cash revenue.
 */
export interface CustomerManagerMoneyTrendDay {
  date: string;
  usage_count: number;
  usd_charged_micros: number;
  usd_charged_cents: number;
}

/**
 * One owned customer ranked by 30-day consumption. `org_id` is null when
 * the revenue ledger knows an account that the scoped customer list does
 * not return — an account with no org, or one outside the listed book in
 * an admin's cross-book view. The console then renders the row without a
 * Customer 360 deep link.
 *
 * A null here is NOT an ownership state, and in particular does not mean
 * "released": the owner-scoped revenue aggregate resolves accounts
 * through live assignments only (`revoked_at IS NULL`), so a released
 * customer leaves this ranking altogether rather than appearing without
 * an org.
 */
export interface CustomerManagerMoneyTopCustomer {
  account_id: string;
  email: string;
  org_id: string | null;
  org_name: string | null;
  usage_count: number;
  usd_charged_micros: number;
  usd_charged_cents: number;
}

/**
 * Owned-book activity funnel (issue #1065 / epic #1035 goal 7): the
 * activity segment only. The new-registration cohort counters stay an
 * admin-only semantics and are not surfaced here.
 */
export interface CustomerManagerMoneyFunnel {
  window_days: number;
  owned_customers: number;
  active_customers: number;
  paying_customers: number;
  /** `paying / active`; null when there is no active customer. */
  paying_rate: number | null;
}

/**
 * `GET /v1/ops/v2/customer-manager/money` — the workbench money view.
 * Scope is injected server-side via the E1-S4 mechanism: customer
 * managers are pinned to `ownerOpsUserId = self`, sales leads to their
 * sales-profile brand, and client-supplied scope params are ignored
 * for non-admin sessions.
 */
export interface CustomerManagerMoneyResponse {
  trend_days: CustomerManagerMoneyTrendDays;
  trend: CustomerManagerMoneyTrendDay[];
  aggregate_window: {
    days: number;
    from: string;
    to: string;
  };
  top_customers: CustomerManagerMoneyTopCustomer[];
  funnel: CustomerManagerMoneyFunnel;
  generated_at: string;
}

// ─── workbench runway / 催充值 list (epic #1035 E6-D2, #1066) ──────────────

/**
 * Days-left thresholds offered by the workbench 催充值 panel. Filtering
 * is client-side — the endpoint always returns the whole owned book so
 * the threshold toggle never refetches.
 */
export const CUSTOMER_MANAGER_RUNWAY_THRESHOLD_OPTIONS = [7, 14, 30] as const;
export type CustomerManagerRunwayThresholdDays =
  (typeof CUSTOMER_MANAGER_RUNWAY_THRESHOLD_OPTIONS)[number];
export const CUSTOMER_MANAGER_RUNWAY_DEFAULT_THRESHOLD_DAYS: CustomerManagerRunwayThresholdDays = 14;

/**
 * One owned customer's burn-based runway: `days_left` = remaining
 * credit ÷ recent burn rate, computed by the platform runway
 * projection (#1004) under the ownership join (#1066). Fields are
 * tenant-money facts only (remaining / burn / burn-per-day /
 * days-left, USD cents + micros) — no procurement cost, upstream
 * identity, or margin field ever crosses this boundary (issue #1066
 * AC 5). CNY display derives client-side from the
 * `exchange_rate_policy.cny_per_usd` pivot and is labeled consumption.
 */
export interface CustomerManagerRunwayCustomer {
  account_id: string;
  email: string;
  /** Customer 360 deep-link target. Null only on the admin-unscoped
   * fallback (no `ownerOpsUserId` drill-down); the console then
   * renders the row without a deep link. */
  org_id: string | null;
  org_name: string | null;
  remaining_usd_micros: number;
  remaining_usd_cents: number;
  burn_usd_micros: number;
  burn_usd_cents: number;
  burn_per_day_usd_micros: number;
  burn_per_day_usd_cents: number;
  /** Always finite on the chase list; 0 for an account that ran dry but
   * kept burning — the most chase-worthy row of all. Precise
   * `?accountId=` reads use {@link CustomerManagerPreciseRunwayCustomer}
   * where `null` means no burn in the window (infinite runway). */
  days_left: number;
}

/**
 * Precise Customer 360 runway row (#2155). Same tenant-money facts as
 * {@link CustomerManagerRunwayCustomer}, but `days_left` may be `null`
 * when the burn window saw no consumption — infinite runway, not
 * missing data. The chase list never returns this shape.
 */
export type CustomerManagerPreciseRunwayCustomer = Omit<
  CustomerManagerRunwayCustomer,
  'days_left'
> & {
  readonly days_left: number | null;
};

/**
 * `GET /v1/ops/v2/customer-manager/runway?accountId=` — one account.
 * Discriminated from the chase list by `mode: 'account'` so a precise
 * payload cannot type-check as {@link CustomerManagerRunwayResponse}.
 */
export interface CustomerManagerPreciseRunwayResponse {
  readonly mode: 'account';
  burn_window: OpsRunwayBurnWindow;
  customers: CustomerManagerPreciseRunwayCustomer[];
  generated_at: string;
}

/**
 * `GET /v1/ops/v2/customer-manager/runway` — the workbench 催充值
 * (top-up chase) list. Scope is injected server-side via the E1-S4
 * mechanism: customer managers are pinned to `ownerOpsUserId = self`;
 * sales leads get the brand book via a per-manager assignments
 * fan-out; client-supplied scope params are ignored for non-admin
 * sessions. Rows are ranked `days_left` ASC and NEVER truncated for
 * sales sessions (epic #1035 AC 2).
 */
export interface CustomerManagerRunwayResponse {
  readonly mode: 'list';
  burn_window: OpsRunwayBurnWindow;
  customers: CustomerManagerRunwayCustomer[];
  generated_at: string;
}

// ─── server-determined view scope (epic #1030 E1-S4, #1040) ───────────────

/**
 * The scope ops-api enforces on every customer-facing list/detail route for
 * the current session. Served by `GET /v1/ops/customers/context` so the
 * ops-console pool/workbench/ownership pages can render the brand context
 * without ever trusting client-side state:
 *
 *   - `kind: 'admin'` — cross-brand; `brand` is always null and the operator
 *     may filter freely.
 *   - `kind: 'finance'` (#1958) — cross-brand READ for the finance manager,
 *     who signs stage 2 of every credit application and has no sales book to
 *     be narrowed by. `brand` is null and `self_scoped` is false, same as
 *     admin; the difference is that ops-api refuses every write under the
 *     customer prefixes for this session, so a client that renders a write
 *     control here is offering a button that can only 403. The ops-console
 *     360 page therefore hides them behind `CUSTOMER_MANAGER_ROLES`. This
 *     field is scope, not permission: do not derive write rights from it.
 *   - `kind: 'sales'` — `brand` is the server-side sales-profile brand.
 *     `self_scoped: true` for a plain customer manager (own book only),
 *     `false` for a sales lead (whole own-brand book).
 *
 * A sales user without a profile never reaches this shape: the endpoint
 * fails closed with FORBIDDEN.
 */
export interface OpsCustomerScopeContext {
  kind: 'admin' | 'finance' | 'sales';
  brand: BrandId | null;
  self_scoped: boolean;
}

export interface GetOpsCustomerScopeContextResponse {
  scope: OpsCustomerScopeContext;
}

// ─── CRM contacts + follow-up notes (epic #1035 E6-D5, #1069) ─────────────

/**
 * One editable contact card per customer org — who to call and on what
 * channel. Upserted via
 * `PUT /internal/customer-managers/customers/:orgId/contact`;
 * `updated_by_ops_user_id` is stamped from the X-Ops-User-Id header
 * ops-api forwards, never from the payload.
 */
export interface CustomerContactRecord {
  org_id: string;
  contact_name: string | null;
  wechat: string | null;
  phone: string | null;
  contact_email: string | null;
  company: string | null;
  remark: string | null;
  updated_by_ops_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface GetCustomerContactResponse {
  /** `null` when no card has been saved for the org yet. */
  contact: CustomerContactRecord | null;
}

/**
 * Full-card upsert payload: the edit dialog always submits every field,
 * and an absent/empty field clears to null. No author field on purpose —
 * attribution is header-derived server-side.
 */
export interface UpsertCustomerContactRequest {
  contact_name?: string | null;
  wechat?: string | null;
  phone?: string | null;
  contact_email?: string | null;
  company?: string | null;
  remark?: string | null;
}

export interface UpsertCustomerContactResponse {
  contact: CustomerContactRecord;
}

/**
 * One append-only follow-up note. There is no update or delete shape
 * anywhere in this contract by design (plain-text v1, epic #1035 E6-D5);
 * author + timestamp are stamped server-side.
 */
export interface CustomerNoteRecord {
  id: string;
  org_id: string;
  author_ops_user_id: string;
  author_email: string | null;
  author_display_name: string | null;
  body: string;
  created_at: string;
}

export interface ListCustomerNotesResponse {
  /** Newest-first (created_at DESC, id DESC tie-break). */
  notes: CustomerNoteRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateCustomerNoteRequest {
  body: string;
}

export interface CreateCustomerNoteResponse {
  note: CustomerNoteRecord;
}

// ─── customer transfer candidates (epic #1030 E1-S5, #1041) ───────────────

/**
 * One ops user a `customer_transfer` request may name as its transfer
 * target: holds customer-manager authority (directly or via role
 * expansion), is not disabled, and has a sales profile in the requested
 * brand. Served by
 * `GET /internal/customer-managers/transfer-candidates?brand=…` and
 * surfaced to sales sessions through
 * `GET /v1/ops/v2/customer-manager/transfer-candidates`, where ops-api
 * pins `brand` to the session's sales-profile brand (admins pass an
 * explicit `brand`). Cross-brand transfers are blocked by design.
 */
export interface CustomerTransferCandidate {
  ops_user_id: string;
  email: string;
  display_name: string;
  /** Mirrors `customer_manager_login_methods` on the customer summary; additive. */
  login_methods?: OpsLoginMethod[];
  /** Mirrors `customer_manager_contact_email` on the customer summary; additive. */
  contact_email?: string | null;
  brand: BrandId;
}

export interface ListCustomerTransferCandidatesResponse {
  candidates: CustomerTransferCandidate[];
}
